import { NavLink, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getTodoById } from 'core/API/todo';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Divider,
    Button,
    Paper,
    Grid,
    TextField,
} from '@mui/material';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import styles from '../addToDo/AddToDo.module.scss';
import { formatDateWith12HourTime } from 'shared/utils/dateUtils';

type RouteParams = {
    id: string;
};

const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return formatDateWith12HourTime(dateStr);
};

const formatDateMMDDYYYY = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatTimeHHMM = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

// Extract raw due date string from various possible API shapes
const getRawDueDate = (todo: any): string | undefined => {
    return (
        todo?.due_date ??
        todo?.dueDate ??
        todo?.due_at ??
        todo?.dueOn ??
        todo?.due
    );
};

const hasExplicitTime = (dateStr?: string) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    return !(d.getHours() === 0 && d.getMinutes() === 0);
};

const getRawDueTime = (todo: any): string | undefined => {
    return todo?.dueTime ?? todo?.due_time ?? undefined;
};

const computeDueTimeDisplay = (todo: any): string => {
    const t = getRawDueTime(todo);
    if (typeof t === 'string' && t.trim() !== '') {
        // Expecting HH:MM or HH:MM:SS → show HH:MM
        const parts = t.split(':');
        return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : t;
    }
    const d = getRawDueDate(todo);
    if (typeof d === 'string' && d.includes('T')) {
        const match = d.match(/T(\d{2}:\d{2})/);
        if (match && match[1]) return match[1];
    }
    return '--:--';
};

const GetSingleTodo = () => {
    const { id } = useParams<RouteParams>();

    const {
        data: todo,
        isLoading,
        error,
    } = useQuery(['todo', id], () => getTodoById(Number(id)), {
        enabled: !!id,
    });

    if (isLoading) {
        return (
            <div style={{
                width: "100%",
                height: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <Box mt={4}>
                <Alert severity="error">Failed to load ToDo details.</Alert>
            </Box>
        );
    }

    const assignedType = todo?.assignedGroup ? 'Group' : 'Person';
    const assignedValue = todo?.assignedGroup
        ? (todo.assignedGroup?.label || todo.assignedGroup?.name || '')
        : (todo?.user ? `${todo.user?.firstName || ''} ${todo.user?.lastName || ''}`.trim() : '');
    const categoryValue = todo?.category?.label || todo?.category?.name || todo?.category?.title || '';

    const rawDueDate = getRawDueDate(todo);
    const readonlyInputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.23)',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.23)',
        },
    } as const;

    return (
        <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                >
                    <NavLink
                        to={ROUTES.TO_DO}
                        style={{ textDecoration: 'none', color: 'white', padding: '2px 12px' }}
                    >
                        Back
                    </NavLink>
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} className={styles.schedulerTitle}>
                View To-Do
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Assignment Type"
                        value={assignedType}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label={assignedType === 'Person' ? 'Selected Person' : 'Selected Group'}
                        value={assignedValue}
                        fullWidth
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Description"
                        fullWidth
                        value={todo?.description || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Due Date"
                        fullWidth
                        value={formatDateMMDDYYYY(rawDueDate)}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Due Time (24-hour)"
                        fullWidth
                        value={computeDueTimeDisplay(todo)}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Priority"
                        fullWidth
                        value={todo?.priority?.label || todo?.priority?.name || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Status"
                        fullWidth
                        value={todo?.status?.label || todo?.status?.name || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={6}>
                    <TextField
                        label="Category"
                        fullWidth
                        value={categoryValue}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        rows={3}
                        value={(todo as any)?.note?.message || (todo as any)?.note || ''}
                        InputProps={{ readOnly: true }}
                        sx={readonlyInputSx}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default GetSingleTodo;